// ============================================================================
// FinOps — Technology > FinOps module (cloud & tooling cost dashboard)
// Response shapes for the `finops` tRPC router. Money is always USD.
// ============================================================================

/**
 * GCP project IDs pinned at the top of the GCP tab. Hand-picked; empty by
 * default, in which case no "featured" section renders at all.
 */
export const FEATURED_GCP_PROJECTS: string[] = [];

/** Which GCP billing account a row came from. */
export type GcpBillingGroup = 'brkaway' | 'tech';

export type FinopsProvider = 'aws' | 'gcp' | 'vercel';

// ─── Overview (Ramp — "Tech & Product - Tools" card only) ────────────────────

export interface FinopsOverviewSummary {
  /** Month the numbers describe, YYYY-MM. */
  period: string;
  monthTotal: number;
  /** Fractional MoM change vs the previous month; null when there is no prior month. */
  momDelta: number | null;
  /** Sum of the 12 months ending at `period`. */
  trailing12Total: number;
  topVendor: { vendor: string; cost: number } | null;
}

export interface FinopsTrendPoint {
  /** YYYY-MM */
  month: string;
  total: number;
}

export interface FinopsVendorRow {
  vendor: string;
  category: string | null;
  cost: number;
  priorCost: number;
  /** Fractional change vs the prior month; null when the vendor is new. */
  momDelta: number | null;
}

export interface FinopsCategoryRow {
  category: string;
  cost: number;
  /** Fraction of the month total (0..1). */
  share: number;
}

// ─── Cloud tabs (AWS + GCP share these shapes) ───────────────────────────────

export interface FinopsAccountRow {
  id: string;
  name: string;
  cost: number;
}

export interface FinopsProjectRow {
  id: string;
  name: string;
  cost: number;
  billingGroup: GcpBillingGroup;
  featured: boolean;
}

export interface FinopsServiceRow {
  service: string;
  cost: number;
}

export interface FinopsMatrixRow {
  /** AWS account id / GCP project id. */
  accountId: string;
  service: string;
  cost: number;
}

export interface FinopsDailyPoint {
  /** YYYY-MM-DD */
  date: string;
  cost: number;
}

export interface FinopsMonthBreakdown {
  /** YYYY-MM */
  period: string;
  accounts: FinopsAccountRow[];
  services: FinopsServiceRow[];
  matrix: FinopsMatrixRow[];
  total: number;
  /**
   * AWS only, default view: the month that SHOULD be showing (the prior calendar month).
   * Differs from `period` when that month isn't collected yet and the reader fell back
   * to the latest one on record.
   */
  expectedPeriod?: string;
  /**
   * AWS only: true once the fallback is no longer explained by the schedule (the monthly
   * collector runs on the 5th) — the UI says so instead of passing an old month off as
   * "previous month" (bug 06/10/2026: Jul shown in October).
   */
  stale?: boolean;
}

export interface FinopsWeekBreakdown {
  /** Monday of the week, YYYY-MM-DD. */
  period: string;
  accounts: FinopsAccountRow[];
  services: FinopsServiceRow[];
  matrix: FinopsMatrixRow[];
  daily: FinopsDailyPoint[];
  total: number;
  /** Total of the previous Mon–Sun week, for the WoW delta. */
  priorTotal: number;
}

/** GCP variants carry projects (with billing group + featured pin) instead of accounts. */
export interface FinopsGcpMonthBreakdown extends Omit<FinopsMonthBreakdown, 'accounts'> {
  projects: FinopsProjectRow[];
}

export interface FinopsGcpWeekBreakdown extends Omit<FinopsWeekBreakdown, 'accounts'> {
  projects: FinopsProjectRow[];
}

export interface FinopsResourceRow {
  resourceId: string;
  service: string;
  /** AWS account id / GCP project id. */
  accountId: string;
  accountName: string;
  cost: number;
  /** AWS only — rows bucketed below the per-service top-N cut-off. */
  isOther?: boolean;
  /** GCP only. */
  billingGroup?: GcpBillingGroup;
}

export interface FinopsAwsForecast {
  /** Month the forecast covers, YYYY-MM. */
  period: string;
  /** Date the snapshot was taken, YYYY-MM-DD — lets the UI flag a stale forecast. */
  snapshotDate: string;
  /** CE's whole-month forecast (MONTHLY granularity includes the actuals so far). 0 = none. */
  mean: number;
  /** 80% interval, whole-month totals (already include the month to date). */
  lower: number;
  upper: number;
  /** Month to date — context only, never added to `mean`. */
  mtdActual: number;
  priorMonthActual: number;
  /** What the month is projected to total: `mean`, or `mtdActual` when there is no forecast. */
  projected: number;
  currency: string;
  /** The snapshot is not for the current month, or is older than the daily cron explains. */
  stale: boolean;
}

export interface FinopsGcpForecast {
  /** Month the run-rate covers, YYYY-MM. */
  period: string;
  projected: number;
  mtdActual: number;
  priorMonthActual: number;
  dailyAvg7d: number;
  daysElapsed: number;
  daysInMonth: number;
  /** Vercel only — MTD `billed_cost`, i.e. what hit the invoice past the included credit. */
  billedMtd?: number;
  /**
   * Vercel only — last day present in the month, YYYY-MM-DD; null when the month has no rows
   * yet. `daysElapsed` counts through this date rather than through today (vercel_daily is
   * collector-written and always lags a day), so the UI can flag a stalled feed.
   */
  coveredThrough?: string | null;
}

/** Provider-neutral name for the run-rate shape — GCP and Vercel share it. */
export type FinopsRunRateForecast = FinopsGcpForecast;

/** The Vercel tab's name for the same shape. */
export type FinopsVercelForecast = FinopsGcpForecast;

/**
 * One row of the Vercel usage drawer: what a meter actually consumed, and what that cost.
 * Rows with real consumption but no cost are kept — that is the "within the included
 * allotment" case the drawer exists to show.
 */
export interface FinopsUsageRow {
  service: string;
  /** Project name, or '(team)' for charges Vercel does not attribute to a project. */
  project: string;
  quantity: number;
  /** `consumed_unit`; null for meters that carry no unit (e.g. the `Pro` plan line). */
  unit: string | null;
  cost: number;
}

export interface FinopsHistory {
  provider: FinopsProvider;
  monthly: Array<{ month: string; cost: number }>;
  weekly: Array<{ weekStart: string; cost: number }>;
}

/** Union kept open — `vercel_spend` / `gcp_budget` are planned. */
export type FinopsAlertKind = 'health';

export type FinopsAlertSeverity = 'critical' | 'warning' | 'info';

export type FinopsAlertStatus = 'upcoming' | 'open' | 'resolved';

export interface FinopsAlertEntity {
  value: string;
  /** PENDING | RESOLVED | IMPAIRED | UNIMPAIRED | UNKNOWN */
  status: string;
  lastUpdatedTime: string | null;
}

export interface FinopsAlert {
  /** `${eventArn}|${affectedAccount}` */
  id: string;
  kind: FinopsAlertKind;
  severity: FinopsAlertSeverity;
  status: FinopsAlertStatus;
  service: string;
  title: string;
  eventTypeCode?: string;
  accountId: string;
  accountName: string;
  region?: string;
  /** ISO; for a scheduled change this is the deadline (Health's `startTime`). */
  deadline?: string;
  lastUpdatedTime: string;
  description?: string;
  actionability?: string;
  personas?: string[];
  entities?: FinopsAlertEntity[];
  pendingCount?: number;
  resolvedCount?: number;
}

export interface FinopsAlertsSummary {
  openCritical: number;
  openWarning: number;
  deadlinesNext30d: number;
  accountsAffected: number;
  /** MIN(first_seen_at); null until the first event lands. */
  healthFeedSince: string | null;
}

/** `open` covers both `open` and `upcoming` — the same set the Overview panel shows. */
export type FinopsAlertStatusFilter = 'open' | 'resolved' | 'all';

export interface FinopsAlertsFilters {
  status?: FinopsAlertStatusFilter;
  severity?: FinopsAlertSeverity;
  service?: string;
  accountId?: string;
}

export interface FinopsAlertsPage {
  items: FinopsAlert[];
  /** Opaque keyset cursor; null on the last page. */
  nextCursor: string | null;
}

export interface FinopsAlertsFacets {
  services: string[];
  accounts: Array<{ accountId: string; accountName: string }>;
}

/**
 * Lambda is deliberately absent: a deprecated runtime loses patches and
 * eventually blocks create/update, but carries no direct charge.
 */
export const EXTENDED_SUPPORT_SERVICES = [
  'RDS',
  'EKS',
  'OPENSEARCH',
  'ELASTICACHE',
  'MEMORYDB',
] as const;

export interface FinopsHealthClassifierInput {
  eventTypeCode: string;
  service: string;
  eventTypeCategory: string;
  actionability?: string | null;
  personas?: string[] | null;
}

/** First match wins (alerts plan §6.2). */
export const FINOPS_SEVERITY_RULES: ReadonlyArray<{
  id: string;
  severity: FinopsAlertSeverity;
  label: string;
  match: (input: FinopsHealthClassifierInput) => boolean;
}> = [
  {
    id: 'extended_support',
    severity: 'critical',
    label: 'End of standard support on a service that bills for extended support',
    match: (i) =>
      i.eventTypeCode.endsWith('_PLANNED_LIFECYCLE_EVENT') &&
      (EXTENDED_SUPPORT_SERVICES as readonly string[]).includes(i.service),
  },
  {
    id: 'billing_persona',
    severity: 'critical',
    label: 'AWS flagged the event for the billing persona',
    match: (i) => (i.personas ?? []).includes('BILLING'),
  },
  {
    id: 'action_required',
    severity: 'warning',
    label: 'Action required (runtime deprecations, retirements, certificate expiry…)',
    match: (i) => i.actionability === 'ACTION_REQUIRED',
  },
  {
    id: 'scheduled_change',
    severity: 'warning',
    label: 'Any other scheduled change',
    match: (i) => i.eventTypeCategory === 'scheduledChange',
  },
];

export function severityForHealthEvent(
  input: FinopsHealthClassifierInput
): FinopsAlertSeverity {
  for (const rule of FINOPS_SEVERITY_RULES) {
    if (rule.match(input)) return rule.severity;
  }
  return 'info';
}
